// React Imports
import React, { useEffect, useState } from "react";
import { addDays, format, isValid, parse } from "date-fns";
import { manifestDetailsApi } from "src/common/api/afn.api";
import { infoDataType, styleKeys } from "src/common/dao/mfn/mfn-dao.type";
import { TableRendererUtil } from "src/components/helpers/table-renderer-util/table-renderer-util";
import "src/index.scss";
import { translateStrings } from "src/utils/dao-utils";
import { showActionBarComponentsForTableType } from "../afn.component.type";
import Loader from "@amzn/meridian/loader";
import Text from "@amzn/meridian/text";
import Column from "@amzn/meridian/column";
import { getRealmForRegion } from "src/utils/utils";
import { isAftxDomain } from "src/utils/aftx-utils";
import { isFcMenuDomain } from "src/utils/fc-utils";
import {
    MANIFESTING_CONSOLE_BASE_URL,
    MANIFESTING_CONSOLE_REGION,
    CONSOLE_WINDOW_DAYS,
} from "src/common/constants/manifesting-console.const";
import { EAGLEEYE_PROD_HOSTNAME } from "src/common/constants/utils.const";

// Stage-match EagleEye -> Manifesting Console by hostname: prod host -> prod, a
// gamma host -> gamma, everything else (integ / local) -> beta, so a user stays
// within their own stage's data.
const manifestingConsoleBaseUrl = (): string => {
    const host = window.location.hostname;
    const stage = host === EAGLEEYE_PROD_HOSTNAME ? "prod" : host.includes("gamma") ? "gamma" : "beta";
    return MANIFESTING_CONSOLE_BASE_URL[stage];
};

// Parse the leading YYMMDD of a TCDA id. Strict: null for a missing id or an
// impossible date (e.g. 02/30).
const parseTcdaDate = (tcdaId?: string): Date | null => {
    const datePrefix = tcdaId?.slice(0, 6) ?? "";
    if (!/^\d{6}$/.test(datePrefix)) return null;
    const date = parse(`20${datePrefix}`, "yyyyMMdd", new Date());
    return isValid(date) && format(date, "yyMMdd") === datePrefix ? date : null;
};

const toIsoDate = (date: Date): string => format(date, "yyyy-MM-dd");

// A CONSOLE_WINDOW_DAYS-day lookup window from the TCDA creation date, or null
// when the id has no valid date (console then falls back to its default window).
const consoleWindowFromTcdaId = (tcdaId?: string): { start: string; end: string } | null => {
    const start = parseTcdaDate(tcdaId);
    if (!start) return null;
    const end = addDays(start, CONSOLE_WINDOW_DAYS - 1);
    return { start: toIsoDate(start), end: toIsoDate(end) };
};

export const ManifestDetails = (props: any) => {
    let { realm, shipmentId, encryptedShipmentId, tcdaId } = props;
    const region = realm == "fe" ? "jp" : realm
    const consoleRegion = MANIFESTING_CONSOLE_REGION[String(realm).toLowerCase()];
    const consoleWindow = consoleWindowFromTcdaId(tcdaId);
    // EagleEye surface only — hidden on the AFTX and FC-menu surfaces (EagleEye serves those domains too).
    const manifestingConsoleUrl = consoleRegion && encryptedShipmentId && !isAftxDomain() && !isFcMenuDomain()
        ? `${manifestingConsoleBaseUrl()}/manifest/container/${encodeURIComponent(encryptedShipmentId)}?region=${consoleRegion}` +
          (consoleWindow ? `&start=${consoleWindow.start}&end=${consoleWindow.end}` : "")
        : "";
    useEffect(() => {
        manifestDetailsApiCall();
    }, []);
    const [manifestData, setManifestData] = useState<any>(null);
    const manifestDetailsApiCall = async () => {
        if (!shipmentId) {
            setManifestData([{ label: "Manifest details", value: "Shipment ID is not available and cannot get manifest", type: "highLightedText", width: 20 }]);
            return;
        }
        const data = await manifestDetailsApi(shipmentId);
        setManifestData(createManifestGrid(data));
    };

    function createManifestGrid(manifestData: any): any {
        if (manifestData.message) return ""
        let manifestTableList: Array<Record<string, infoDataType>> = []
        let styles: Record<styleKeys, string> = {
            wordWrap: 'break-word',
            fontSize: '13px'
        }
        Object.keys(manifestData["legs"]).map((leg: any) => {
            if (manifestData["legs"][leg]["manifestDetails"]) {
                Object.keys(manifestData["legs"][leg]["manifestDetails"]).map((id: any) => {
                    let manifestInfo = manifestData["legs"][leg]["manifestDetails"][id]
                    manifestTableList.push(translateStrings({
                        nodeId: { label: "Node", value: manifestData["legs"][leg]["leg"], styles },
                        manifestId: { label: "Manifest ID", value: manifestInfo["manifestId"]? manifestInfo["manifestId"]: "", styles },
                        documentType: { label: "Document Type", value: manifestInfo["documentType"]? manifestInfo["documentType"]: "", styles },
                        transmissionType: { label: "Transmission Type", value: manifestInfo["EDITransmissionDetails"] && manifestInfo["EDITransmissionDetails"]["Error"] != true ? "EDI" : "", styles },
                        transmissionStatus: { label: "Transmission Status", value: getTransmissionStatus(manifestInfo["EDITransmissionDetails"], manifestInfo["manifestId"], manifestInfo["fileName"]), styles },
                    }))
                })
            }
        })
        if (manifestTableList.length === 0) {
            return [{ label: "Manifest details", value: "No manifest details found.", type: "highLightedText", width: 20 }];
        }
        return [{ label: "Manifest details", value: manifestTableList, type: "tableInCell", width: 200 }];
    }
    const showActionBarComponentsForTable: showActionBarComponentsForTableType = {
        "showActionBar": false,
        "showSearchBar": false,
        "showFilters": false,
        "showDownloadButton": false,
        showColumnFilters:true
    }

    function getTransmissionStatus(EDITransmissionDetails: any, manifestId: string, fileName: any): string {
        if (!EDITransmissionDetails)
            return ""
        
        if (EDITransmissionDetails["Error"] && fileName) {
            let irisLink = `<a href="https://${getRealmForRegion(region)}.prod.transcentral.ctt.amazon.dev/message-listing?operation=OR&FILE_NAME=${fileName}&type=relative&amount=1&unit=month&key=one-month" target="_blank">IRIS</a>`;
            return `Not via Lucidas, check in ${irisLink}`;
        }
        const status = EDITransmissionDetails["transmissionStatus"];
        if (status === "Restricted") return status

        if (status === "Delivered") {
            const timestamp = new Date(EDITransmissionDetails.deliveryCompleteTimestamp || "");
            
            const formData = {
                dateRange: {
                    type: "absolute",
                    startDate: new Date(new Date(timestamp).setDate(timestamp.getDate() - 10)).toISOString().split('T')[0],
                    endDate: new Date(new Date(timestamp).setDate(timestamp.getDate() + 10)).toISOString().split('T')[0]
                },
                objectType: "TRIGGER_EVENT",
                objectValues: `${EDITransmissionDetails.triggerEvent}`
            };
            
            const lucidaslink = `<a href="https://${region}.console.link.scot.a2z.com/lucidus/event?form=${encodeURIComponent(JSON.stringify(formData))}" target="_blank">`;

            const receiptId = EDITransmissionDetails.receiptId;
            const lucidasDeliveredLink = `${lucidaslink}Delivered</a>`;
            if (receiptId) {
                const lucidaslinkWithReceipt = `${lucidaslink}${receiptId}</a>`;
                return `Delivered to the Carrier <br> <b> MDN:</b> ${lucidaslinkWithReceipt}`;
            } else {
                if (EDITransmissionDetails.receiverEndpointReference.includes("SFTP"))
                    return `${lucidasDeliveredLink} to the Carrier (SFTP Endpoint)`;
                else
                    return `${lucidasDeliveredLink} to the Carrier`;
            }
        }

        else if (status === "Error") {
            const eventId = EDITransmissionDetails.eventId || manifestId;
            const tt_link = `<a href="https://t.corp.amazon.com/issues?q=extensions.tt.status%3A%28Assigned%20OR%20Researching%20OR%20%22Work%20In%20Progress%22%20OR%20Pending%20OR%20Resolved%20OR%20Closed%29%20AND%20currentSeverity%3A%281%20OR%202%20OR%203%20OR%204%20OR%205%20OR%202.5%20OR%20N%29%20AND%20full_text%3A%28${eventId}%29%20AND%20extensions.tt.assignedGroup%3A%28TransEDI_Operations%20OR%20EDITransportation%29" target="_blank"> link </a>`;
            return `Error <br> <b> Trouble Ticket: </b>${tt_link}`;
        }

        else if (status === "PendingDelivery"){
            const tt_link = `<a href="https://t.corp.amazon.com/issues?q=extensions.tt.status%3A%28Assigned%20OR%20Researching%20OR%20%22Work%20In%20Progress%22%20OR%20Pending%29%20AND%20currentSeverity%3A%281%20OR%202%29%20AND%20full_text%3A%28${EDITransmissionDetails.originalReceiverBusinessUnitReference}%29%20AND%20extensions.tt.assignedGroup%3A%28TransEDI_Operations%20OR%20EDITransportation%29" target="_blank">link</a>`;
            return `Pending Delivery <br> <b> Trouble Ticket: </b>${tt_link}`;
        }
        else {
            return status
        }
    }

    return (
        <div className="table-render">
            {
                manifestData === null &&
                <Text alignment="center">
                        <pre /> 
                        <Loader type="circular" size="large" /> 
                     Loading... this may take a while... 
                </Text>
            }
            {
                manifestData &&
                <TableRendererUtil
                    tableData={[manifestData]}
                    onFiltersClick={() => { }}
                    tableHeaders={[]}
                    showActionBarComponents={showActionBarComponentsForTable}
                    numberOfPages={-1}
                    onTablePageChange={() => { }}
                    isRowHeader={true}
                />
            }
            {
                manifestingConsoleUrl &&
                <Column alignmentHorizontal="center" spacingInset="400 none">
                    <Text type="b300" alignment="center">
                        <a href={manifestingConsoleUrl} target="_blank" rel="noopener noreferrer">
                            See this container's full timeline in Manifesting Console →
                        </a>
                    </Text>
                </Column>
            }
        </div>
    );
};
